import type {
  CmmdClaudeCredentialState,
  CmmdCodexCredentialState,
  CmmdProviderAccountInventory,
} from "../../environments/cmmdApi";
import { resolveConnectedAccountEmail } from "../../lib/connectedAccountEmail";
import type { ProviderAccountListItem } from "./ProviderAccountManagePanel";

export type CmmdProviderAccountInventoryAccount = CmmdProviderAccountInventory["accounts"][number];

export interface ClaudeCloudAccountState {
  readonly cloudConnected: boolean;
  readonly cloudManageable: boolean;
  /**
   * Whether this user has a Claude account on file at all, usable or not.
   *
   * Deliberately NOT `claudeAccountRows.length > 0`: for a never-connected user
   * `resolveProviderAccountRows` synthesises a placeholder row with a null email
   * and needsReconnect true, so a length check would greet a brand new user with
   * "Identity unavailable / Reconnect required" instead of the connect card.
   */
  readonly hasKnownAccount: boolean;
  readonly cloudAccountEmail: string | null;
  readonly cloudAccountDisplayName: string | null;
  readonly cloudPlanLabel: string | null;
  readonly claudeAccountSummary: string;
  readonly claudeAccountRows: ReadonlyArray<ProviderAccountListItem>;
}

export function resolveProviderAccountRowSource(input: {
  readonly providerAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly connectedViaCloudStore: boolean;
  readonly hasLocalAccount: boolean;
}): ReadonlyArray<CmmdProviderAccountInventoryAccount> {
  return input.connectedViaCloudStore || !input.hasLocalAccount ? input.providerAccounts : [];
}

export function resolveCodexPersonalSelectionState(input: {
  readonly accessMode: "workspace" | "personal";
  readonly workspaceWalletAllowed: boolean;
  readonly isUsingChatGptAccount: boolean;
  readonly hasUsableProviderAccount: boolean;
  readonly hasLocalAccount: boolean;
}): { readonly workspaceSelected: boolean; readonly personalSelected: boolean } {
  const usesInventoryAccount =
    input.hasUsableProviderAccount && input.accessMode === "personal" && !input.hasLocalAccount;
  const workspaceSelected =
    input.workspaceWalletAllowed && input.accessMode === "workspace" && !usesInventoryAccount;
  const personalSelected = input.workspaceWalletAllowed
    ? input.isUsingChatGptAccount || usesInventoryAccount
    : input.hasLocalAccount || input.accessMode === "personal";
  return { workspaceSelected, personalSelected };
}

export function resolveProviderAccountRows(input: {
  readonly providerAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly accountEmail: string | null;
  readonly planLabel: string | null;
  readonly isDefaultForNewChats: boolean;
  readonly needsReconnect: boolean;
}): ReadonlyArray<ProviderAccountListItem> {
  if (input.providerAccounts.length > 0) {
    const effectiveDefaultAccount =
      input.providerAccounts.find((account) => account.isDefault && !account.needsReconnect) ??
      input.providerAccounts.find((account) => !account.needsReconnect) ??
      input.providerAccounts.find((account) => account.isDefault) ??
      input.providerAccounts[0] ??
      null;
    const effectiveDefaultAccountId = effectiveDefaultAccount?.providerAccountId ?? null;
    return input.providerAccounts.map((account) => ({
      id: account.providerAccountId,
      credentialTarget: account.credentialTarget,
      accountDisplayName: normalizeProviderAccountDisplayName(account.accountDisplayName),
      accountEmail: resolveConnectedAccountEmail(account.accountEmail),
      planLabel: formatProviderPlanLabel(account.planType),
      isDefaultForNewChats: account.providerAccountId === effectiveDefaultAccountId,
      needsReconnect: account.needsReconnect,
      canDisconnect: account.credentialTarget === "primary",
    }));
  }

  if (input.accountEmail === null && input.planLabel === null && !input.needsReconnect) {
    return [];
  }

  return [
    {
      id: input.accountEmail ?? "connected-provider-account",
      credentialTarget: "primary",
      accountDisplayName: null,
      accountEmail: input.accountEmail,
      planLabel: input.planLabel,
      isDefaultForNewChats: input.isDefaultForNewChats,
      needsReconnect: input.needsReconnect,
      canDisconnect: true,
    },
  ];
}

export function resolveDefaultProviderAccountRow(
  rows: ReadonlyArray<ProviderAccountListItem>,
): ProviderAccountListItem | null {
  return (
    rows.find((account) => account.isDefaultForNewChats && !account.needsReconnect) ??
    rows.find((account) => !account.needsReconnect) ??
    rows.find((account) => account.isDefaultForNewChats) ??
    rows[0] ??
    null
  );
}

export function resolveClaudeCloudAccountState(input: {
  readonly claudeCloudActive: boolean;
  readonly connection: CmmdClaudeCredentialState | null;
  readonly providerAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
}): ClaudeCloudAccountState {
  const connectionNeedsReconnect = credentialNeedsReconnect(input.connection);
  const hasSavedConnection = input.connection?.connected === true;
  const display = resolveClaudeDisplayAccount({
    connection: input.connection,
    providerAccounts: input.providerAccounts,
    connectionNeedsReconnect,
  });
  const cloudConnected =
    input.claudeCloudActive &&
    (display.usableConnection !== null || display.usableProviderAccount !== null);
  const hasKnownAccount =
    input.claudeCloudActive && (hasSavedConnection || input.providerAccounts.length > 0);
  // cloudConnected implies one of the two terms above, so this is the same
  // condition; it stays spelled out because "manageable" and "has an account"
  // are different questions that happen to share an answer.
  const cloudManageable = hasKnownAccount || cloudConnected;
  const cloudAccountEmail =
    display.providerAccount === null
      ? resolveConnectionAccountEmail(display.connection)
      : resolveConnectedAccountEmail(display.providerAccount.accountEmail);
  const cloudAccountDisplayName =
    display.providerAccount === null
      ? resolveConnectionAccountDisplayName(display.connection)
      : normalizeProviderAccountDisplayName(display.providerAccount.accountDisplayName);
  const cloudPlanLabel =
    display.providerAccount === null
      ? resolveConnectionPlanLabel(display.connection)
      : formatProviderPlanLabel(display.providerAccount.planType);
  const rowProviderAccounts = resolveClaudeRowProviderAccounts({
    providerAccounts: input.providerAccounts,
    usableConnection: display.usableConnection,
    usableProviderAccount: display.usableProviderAccount,
  });
  const claudeAccountRows = resolveProviderAccountRows({
    providerAccounts: rowProviderAccounts,
    accountEmail: cloudAccountEmail,
    planLabel: cloudPlanLabel,
    isDefaultForNewChats: true,
    needsReconnect: input.connection?.connected === false || connectionNeedsReconnect,
  });

  return {
    cloudConnected,
    cloudManageable,
    hasKnownAccount,
    cloudAccountEmail,
    cloudAccountDisplayName,
    cloudPlanLabel,
    claudeAccountSummary: summarizeProviderAccounts({
      rows: claudeAccountRows,
      fallbackEmail: cloudAccountEmail,
      fallbackDisplayName: cloudAccountDisplayName,
    }),
    claudeAccountRows,
  };
}

function resolveClaudeDisplayAccount(input: {
  readonly connection: CmmdClaudeCredentialState | null;
  readonly providerAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly connectionNeedsReconnect: boolean;
}): {
  readonly providerAccount: CmmdProviderAccountInventoryAccount | null;
  readonly connection: CmmdClaudeCredentialState | null;
  readonly usableConnection: CmmdClaudeCredentialState | null;
  readonly usableProviderAccount: CmmdProviderAccountInventoryAccount | null;
} {
  const usableConnection =
    input.connection?.connected === true && !input.connectionNeedsReconnect
      ? input.connection
      : null;
  const usableProviderAccount = findUsableProviderAccount(input.providerAccounts);
  return {
    providerAccount:
      usableProviderAccount ??
      (usableConnection === null ? findFallbackProviderAccount(input.providerAccounts) : null),
    connection: usableProviderAccount === null ? input.connection : null,
    usableConnection,
    usableProviderAccount,
  };
}

function findUsableProviderAccount(
  providerAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>,
): CmmdProviderAccountInventoryAccount | null {
  return (
    providerAccounts.find((account) => account.isDefault && !account.needsReconnect) ??
    providerAccounts.find((account) => !account.needsReconnect) ??
    null
  );
}

function findFallbackProviderAccount(
  providerAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>,
): CmmdProviderAccountInventoryAccount | null {
  return providerAccounts.find((account) => account.isDefault) ?? providerAccounts[0] ?? null;
}

function normalizeProviderAccountDisplayName(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function credentialNeedsReconnect(
  credential: CmmdCodexCredentialState | CmmdClaudeCredentialState | null,
): boolean {
  return (
    credential?.connected === true && "needsReconnect" in credential && credential.needsReconnect
  );
}

function resolveClaudeRowProviderAccounts(input: {
  readonly providerAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly usableConnection: CmmdClaudeCredentialState | null;
  readonly usableProviderAccount: CmmdProviderAccountInventoryAccount | null;
}): ReadonlyArray<CmmdProviderAccountInventoryAccount> {
  if (input.usableProviderAccount !== null) return input.providerAccounts;
  if (input.usableConnection === null) return input.providerAccounts;
  return [];
}

export function formatProviderPlanLabel(plan: string | null | undefined): string | null {
  const trimmed = plan?.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(/[_-]+/g, " ");
  const core = normalized.replace(/^chatgpt\s+/iu, "").replace(/\s+subscription$/iu, "");
  if (!core || core.toLowerCase() === "unknown") return null;
  return core
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function resolveConnectionAccountEmail(
  connection: CmmdClaudeCredentialState | null,
): string | null {
  return connection?.connected === true
    ? resolveConnectedAccountEmail(connection.accountEmail)
    : null;
}

function resolveConnectionAccountDisplayName(
  connection: CmmdClaudeCredentialState | null,
): string | null {
  if (connection?.connected !== true || !("accountDisplayName" in connection)) return null;
  return normalizeProviderAccountDisplayName(
    connection.accountDisplayName as string | null | undefined,
  );
}

function summarizeProviderAccounts(input: {
  readonly rows: ReadonlyArray<ProviderAccountListItem>;
  readonly fallbackEmail: string | null;
  readonly fallbackDisplayName: string | null;
}): string {
  const usableRows = input.rows.filter((row) => !row.needsReconnect);
  if (usableRows.length > 1) {
    const names = usableRows
      .slice(0, 2)
      .map((row) => row.accountDisplayName ?? row.accountEmail ?? "Identity unavailable")
      .join(", ");
    const suffix = usableRows.length > 2 ? `, +${usableRows.length - 2} more` : "";
    return `${usableRows.length} Claude accounts connected: ${names}${suffix}.`;
  }
  const label = input.fallbackDisplayName ?? input.fallbackEmail ?? "Connected account";
  return `${label} is available to your cloud runtimes.`;
}

function resolveConnectionPlanLabel(connection: CmmdClaudeCredentialState | null): string | null {
  return connection?.connected === true ? formatProviderPlanLabel(connection.planType) : null;
}
